import { type ReactNode } from "react";

/**
 * Wrap occurrences of `quotes` in React children without disturbing
 * structure.
 *
 * Highlighting the markdown *source* would corrupt it — a quote can span an
 * emphasis marker, or contain characters markdown treats as syntax. Instead
 * this runs over already-rendered children and only ever splits plain text
 * nodes, so headings, bold runs and list structure come through untouched.
 */

/** A truncating ellipsis on either end is the judge shortening its quote,
 *  not content — strip it so the rest can match. */
const stripEllipsis = (quote: string) =>
  quote
    .replace(/^(?:\u2026|\.\.\.)\s*/, "")
    .replace(/\s*(?:\u2026|\.\.\.)$/, "")
    .trim();

/** Longest first, so a quote containing another doesn't get chopped up. */
const byLengthDesc = (quotes: readonly string[]) =>
  [...new Set(quotes.filter((q) => stripEllipsis(q).length > 0))].sort(
    // Stripped length: matching runs on the stripped text, and a quote
    // longer only by its ellipsis must not outrank a longer real one.
    (a, b) => stripEllipsis(b).length - stripEllipsis(a).length,
  );

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** JSON-ish quotes match across formatting: the judge quotes the compact
 *  string while the page shows it pretty-printed, so whitespace around
 *  structural characters must be free. */
const JSON_STRUCTURAL = /[{}[\],:]/;

const flexibleJsonPattern = (quote: string) => {
  let out = "";
  for (const ch of quote) {
    if (/\s/.test(ch)) {
      out += "\\s*";
    } else if (JSON_STRUCTURAL.test(ch)) {
      out += `\\s*${escapeRegExp(ch)}\\s*`;
    } else {
      out += escapeRegExp(ch);
    }
  }
  return out.replace(/(?:\\s\*)+/g, "\\s*");
};

/**
 * A numeral must match as a whole number, or highlighting "78" would also
 * paint the 78 inside "782" and inside a date. JSON-ish quotes tolerate any
 * whitespace around structure. Everything else matches literally.
 */
const toPattern = (rawQuote: string) => {
  const quote = stripEllipsis(rawQuote);
  if (/^[\d.]+$/.test(quote)) return `(?<![\\d.])${escapeRegExp(quote)}(?![\\d.])`;
  if (/^[{[]/.test(quote)) return flexibleJsonPattern(quote);
  return escapeRegExp(quote);
};

export const highlightChildren = (children: ReactNode, quotes: readonly string[]): ReactNode => {
  const wanted = byLengthDesc(quotes);
  if (wanted.length === 0) return children;

  const pattern = new RegExp(`(${wanted.map(toPattern).join("|")})`, "gi");

  const walk = (node: ReactNode, key: string): ReactNode => {
    if (typeof node === "string") {
      const parts = node.split(pattern);
      if (parts.length === 1) return node;
      return parts.map((part, index) =>
        // split() with a capture group puts matches at odd indices.
        index % 2 === 1 ? (
          <mark key={`${key}-${index}`} className="rounded bg-globemallow-200 px-0.5 text-text-primary">
            {part}
          </mark>
        ) : (
          part
        ),
      );
    }

    if (Array.isArray(node)) {
      return node.map((child, index) => walk(child, `${key}-${index}`));
    }

    // A React element: recurse into its children, keeping the element itself
    // — that is what preserves the markdown structure.
    if (node && typeof node === "object" && "props" in node) {
      const element = node as React.ReactElement<{ children?: ReactNode }>;
      // Never re-enter a mark. A loose list item renders as li > p, and both
      // tags are wrapped, so the same text was walked twice — the li pass
      // marked it, then the p pass walked the marked children and nested a
      // second mark inside the first. Skipping marks makes the walk
      // idempotent, whatever combination of wrapped containers runs it.
      if (element.type === "mark") return node;
      if (element.props?.children === undefined) return node;
      return {
        ...element,
        props: { ...element.props, children: walk(element.props.children, key) },
      } as ReactNode;
    }

    return node;
  };

  return walk(children, "hl");
};

/**
 * ReactMarkdown `components` overrides that highlight inside the elements
 * markdown produces.
 *
 * Only the leaf text-bearing elements are overridden. Overriding a container
 * like `ul` would re-walk its subtree and double-mark.
 */
export const highlightComponents = (quotes: readonly string[]) => {
  if (quotes.length === 0) return undefined;

  const wrap =
    (Tag: "p" | "li" | "h1" | "h2" | "h3" | "h4" | "td" | "th" | "strong" | "em") =>
    ({ children, ...props }: { children?: ReactNode }) => <Tag {...props}>{highlightChildren(children, quotes)}</Tag>;

  return {
    p: wrap("p"),
    li: wrap("li"),
    h1: wrap("h1"),
    h2: wrap("h2"),
    h3: wrap("h3"),
    h4: wrap("h4"),
    td: wrap("td"),
    th: wrap("th"),
  };
};
